import { useState, useEffect } from 'react'
import { supabase } from '../../../api/supabase'
import { containsAllTokens } from '../../../lib/verification/normalize'
import SearchableSelect from '../../../components/SearchableSelect'
import AddressMappingPanel, { type AddressMappingFields, quickParseAddress, resolveCountryCode } from './AddressMappingPanel'
import { fetchAllRows } from '../../../lib/fetchAllRows'
import CompanyLink from '../../../components/CompanyLink'
import { resolveCountryText } from '../../../lib/countries'

// Hoisted so the >85% similarity scorer (below) can reuse the exact same
// suffix-stripping as the name-matching inside analyzeAddresses().
// Side-by-side "BL/HBL says" vs "address book has" so staff can tell a same-company match from a
// different branch at a glance -- words that appear on only one side are highlighted.
function AddressCompare({ left, right, leftLabel = 'From document', rightLabel = 'Address book' }: {
  left: string; right: string; leftLabel?: string; rightLabel?: string
}) {
  const words = (t: string) => t.toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean)
  const leftSet = new Set(words(left))
  const rightSet = new Set(words(right))
  const render = (text: string, other: Set<string>) =>
    text.split(/(\s+)/).map((tok, i) => {
      const w = words(tok)[0]
      return w && !other.has(w)
        ? <mark key={i} className="bg-amber-200 dark:bg-amber-700/60 text-inherit rounded px-0.5">{tok}</mark>
        : <span key={i}>{tok}</span>
    })
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
      <div className="p-2 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
        <div className="font-semibold text-gray-500 dark:text-gray-400 mb-1">{leftLabel}</div>
        <div className="text-gray-900 dark:text-gray-100 break-words">{left ? render(left, rightSet) : <em className="text-gray-400">(no address)</em>}</div>
      </div>
      <div className="p-2 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
        <div className="font-semibold text-gray-500 dark:text-gray-400 mb-1">{rightLabel}</div>
        <div className="text-gray-900 dark:text-gray-100 break-words">{right ? render(right, leftSet) : <em className="text-gray-400">(no address on file)</em>}</div>
      </div>
    </div>
  )
}

function removeCompanySuffixes(name: string): string {
  return name
    .replace(/\s+(LTD|CO|LIMITED|CORP|INC|LLC|GmbH|AG|SA|LLP|GMBH|SARL|BV|NV|SL|SPA|EIRL|S\.L\.|CO\.?|LTD\.?|INC\.?|LLC\.?|LIMITED\.?)\s*\.?$/i, '')
    .trim()
}

// Character-bigram Dice coefficient — a small, dependency-free way to score how similar two
// company names are (0..1). Used for the "Suggested match" hint on addresses the exact/fuzzy
// name matcher above didn't resolve, to catch near-duplicates (typos, reordered words, minor
// punctuation differences) that would otherwise sail through as a brand new customer.
function normalizeForSimilarity(name: string): string {
  return removeCompanySuffixes(name).toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim()
}
function bigrams(s: string): Map<string, number> {
  const map = new Map<string, number>()
  for (let i = 0; i < s.length - 1; i++) {
    const bg = s.substring(i, i + 2)
    map.set(bg, (map.get(bg) || 0) + 1)
  }
  return map
}
function nameSimilarity(a: string, b: string): number {
  const na = normalizeForSimilarity(a)
  const nb = normalizeForSimilarity(b)
  if (!na || !nb) return 0
  if (na === nb) return 1
  if (na.length < 2 || nb.length < 2) return 0
  const bgA = bigrams(na)
  const bgB = bigrams(nb)
  let intersection = 0
  bgA.forEach((countA, bg) => {
    const countB = bgB.get(bg)
    if (countB) intersection += Math.min(countA, countB)
  })
  return (2 * intersection) / (na.length - 1 + (nb.length - 1))
}
const VAT_MAX = 12   // customers.vat_no varchar(12) + CDS CHECK
const SUGGESTED_MATCH_THRESHOLD = 0.85
// Bug#Z9-4: a near/exact-identical name (>=95%) shouldn't need a manual "Use this" click --
// only genuinely ambiguous matches (85-95%) should require staff to confirm.
const AUTO_SELECT_THRESHOLD = 0.95

interface ExtractedData {
  shipper?: {
    name?: string
    address?: string
    address_line_1?: string | null
    address_line_2?: string | null
    address_line_3?: string | null
    city?: string | null
    postcode?: string | null
    country?: string | null
    eori?: string | null
    [key: string]: any
  }
  consignee?: {
    name?: string
    address?: string
    contact?: string
    address_line_1?: string | null
    address_line_2?: string | null
    address_line_3?: string | null
    city?: string | null
    postcode?: string | null
    country?: string | null
    eori?: string | null
    vat_no?: string | null
    [key: string]: any
  }
  [key: string]: any
}

interface UniqueAddress {
  key: string
  type: 'shipper' | 'consignee'
  name: string
  address?: string | null
  contact?: string | null
  country?: string
  address_line_1?: string | null
  address_line_2?: string | null
  address_line_3?: string | null
  city?: string | null
  postcode?: string | null
  eori?: string | null
  vat_no?: string | null   // typed by staff for a NEW company only (nothing parses it from the file)
  count: number
  status: 'new' | 'matched' | 'ambiguous' | 'address_mismatch'
  matchedCustomer?: { id: string; name: string; code: string; addressDisplay?: string }
  candidates?: Array<{ id: string; name: string; code: string }>
  // >85% name-similar existing customer the exact/fuzzy matcher above didn't already resolve
  // to — see nameSimilarity(). Only computed when status ends up 'new'.
  suggestedMatch?: { id: string; name: string; code: string; score: number }
  selectedCustomerId?: string
  // customers.name has a unique index (idx_customers_normalized_name) — a mismatched-address
  // company can't become a second row, so "use BL address" means updating the existing row,
  // not creating a new one. Undefined until staff resolve the address_mismatch prompt.
  mismatchResolution?: 'use_existing' | 'use_bl'
  // A3/Z52: editable pre-fill for the manual address-mapping panel, shown only when this
  // address is about to become a brand new customer_addresses row (new customer, or a
  // mismatch resolved as "use BL address"). Seeded from AI extraction + a client-side parse
  // of the raw block; staff corrections here are what actually gets saved.
  manualAddress?: AddressMappingFields
  addressManuallyVerified?: boolean
}

interface CustomerMapping {
  key: string
  customerId: string
  role: 'shipper' | 'consignee' | 'notify_party' | 'invoice_to'
}

interface AddressValidationStepProps {
  master: ExtractedData
  details: ExtractedData[]
  onContinue: (addressDecisions: any[], customerMappings?: CustomerMapping[]) => void
  onBack: () => void
}

export default function AddressValidationStep({
  master,
  details,
  onContinue,
  onBack,
}: AddressValidationStepProps) {
  const [addresses, setAddresses] = useState<UniqueAddress[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Full active customer list, kept in state (not just a local var inside analyzeAddresses)
  // so the "Replace from address book" picker below has something to search over.
  const [allCustomers, setAllCustomers] = useState<Array<{ id: string; name: string; code: string }>>([])
  // Existing-customer full address by id, for the side-by-side compare next to a suggested match.
  const [customerAddrById, setCustomerAddrById] = useState<Record<string, string>>({})
  // Which address card currently has its "Replace from address book" picker open — only one
  // at a time, to keep the list from turning into a wall of open dropdowns.
  const [replacingKey, setReplacingKey] = useState<string | null>(null)

  useEffect(() => {
    analyzeAddresses()
  }, [master, details])

  async function analyzeAddresses() {
    try {
      setLoading(true)
      setError(null)

      const uniqueAddresses: Map<string, UniqueAddress> = new Map()

      // Helper to normalize name and country
      function getNormalizedKey(name?: string, country?: string): string {
        if (!name) return ''
        return `${name.toLowerCase().trim()}|${(country || '').toLowerCase().trim()}`
      }

      // Helper to generate map key: {role}_{normalized_name} (without source/detail_index)
      // Must match backend normalization exactly: remove suffixes, lowercase, remove spaces/punctuation
      function getMapKey(type: 'shipper' | 'consignee', name?: string): string {
        if (!name) return ''
        // Remove company suffixes first (e.g., "Ltd", "Inc" → so variants map to same key)
        const nameWithoutSuffix = removeCompanySuffixes(name)
        // Then normalize: lowercase, remove spaces and punctuation
        const normalized = nameWithoutSuffix
          .toLowerCase()
          .trim()
          .replace(/[\s.,()'\"-]+/g, '')  // Remove spaces, dots, commas, parens, quotes, hyphens, dashes
        return `${type}_${normalized}`
      }

      // Helper to remove suffixes (legacy name for compatibility) -- removeCompanySuffixes
      // itself is now hoisted to module scope so nameSimilarity() can reuse it too.
      function fuzzyMatch(name: string): string {
        return removeCompanySuffixes(name)
      }

      // #296: country equality was blocking otherwise-identical name matches. extractCountry()
      // below is a brittle last-line-of-address heuristic that often returns '' or a differently
      // worded value (UK/GB/United Kingdom) between imports of the same real-world address, so a
      // byte-identical company name was showing "Will create new customer". Normalize common UK
      // aliases, and treat either side being blank (extraction failed / not backfilled on the
      // stored row) as a wildcard rather than a mismatch.
      const UK_COUNTRY_ALIASES = new Set(['uk', 'u.k.', 'united kingdom', 'great britain', 'gb', 'england', 'scotland', 'wales', 'northern ireland'])
      function normalizeCountry(country?: string): string {
        const c = (country || '').toLowerCase().trim()
        return UK_COUNTRY_ALIASES.has(c) ? 'gb' : c
      }
      function countriesMatch(a?: string, b?: string): boolean {
        const na = normalizeCountry(a)
        const nb = normalizeCountry(b)
        if (!na || !nb) return true
        return na === nb
      }

      // Helper to extract country from full address (CDS Standard)
      // Z91: returns an ISO-2 code only (never the raw last line -- that is how "M1 4AB GB" ended up in
      // customers.country). Anything that doesn't resolve safely -> '' and the server re-derives it.
      function extractCountry(address?: string): string {
        return resolveCountryText(extractCountryRaw(address)).code ?? ''
      }
      function extractCountryRaw(address?: string): string {
        if (!address) return ''

        // Reject if address looks like an email
        if (address.includes('@')) return ''

        // Extract last line as country (CDS format)
        const lines = address.split('\n').filter(l => l.trim())
        if (lines.length > 0) {
          const lastLine = lines[lines.length - 1].trim()
          // Country is typically the last line, but only if it looks reasonable
          // (not too long, not all caps with numbers, not a full address)
          if (
            lastLine.length > 0 &&
            lastLine.length <= 50 &&  // Country names rarely exceed 50 chars
            !lastLine.includes('HOUSE') &&
            !lastLine.includes('STREET') &&
            !lastLine.includes('BUILDING') &&
            !/\d{5,}/.test(lastLine)  // Not a long sequence of digits
          ) {
            return lastLine
          }
        }

        // Fallback: try comma-separated
        const parts = address.split(',').filter(p => p.trim())
        if (parts.length > 0) {
          const lastPart = parts[parts.length - 1].trim()
          if (
            lastPart.length > 0 &&
            lastPart.length <= 50 &&
            !lastPart.includes('@') &&
            !lastPart.includes('HOUSE') &&
            !lastPart.includes('STREET') &&
            !lastPart.includes('BUILDING') &&
            !/\d{5,}/.test(lastPart)
          ) {
            return lastPart
          }
        }
        return ''
      }

      // Collect all shipper/consignee pairs with source prefix + extracted address fields
      // Z48: `address` (the AI's flat free-text block, e.g. "2 ORIEL COURT, ASHFIELD ROAD,
      // SALE, ENGLAND, M33 7DF, UNITED KINGDOM") is carried through alongside the
      // structured address_line_1/2/3 fields -- BL/PDF parsing only ever populates the
      // flat block (no structured fields), so without this the backend's structured-field
      // path saw address_line_1=undefined and fell back to parsing just the derived
      // `country` string as if it were the whole address (produced address_lines=["UNITED
      // KINGDOM"], dropping the real street/city/postcode entirely).
      const allPairs = [
        {
          source: 'master',
          type: 'shipper' as const,
          name: master.shipper?.name,
          address: master.shipper?.address,
          contact: (master.shipper as any)?.contact,
          country: extractCountry(master.shipper?.address),
          address_line_1: master.shipper?.address_line_1,
          address_line_2: master.shipper?.address_line_2,
          address_line_3: master.shipper?.address_line_3,
          city: master.shipper?.city,
          postcode: master.shipper?.postcode,
          eori: (master.shipper as any)?.eori,
        },
        {
          source: 'master',
          type: 'consignee' as const,
          name: master.consignee?.name,
          address: master.consignee?.address,
          contact: master.consignee?.contact,
          country: extractCountry(master.consignee?.address),
          address_line_1: master.consignee?.address_line_1,
          address_line_2: master.consignee?.address_line_2,
          address_line_3: master.consignee?.address_line_3,
          city: master.consignee?.city,
          postcode: master.consignee?.postcode,
          eori: (master.consignee as any)?.eori,
          vat_no: master.consignee?.vat_no,
        },
      ]

      details.forEach((detail, detailIndex) => {
        allPairs.push(
          {
            source: `detail_${detailIndex}`,
            type: 'shipper' as const,
            name: detail.shipper?.name,
            address: detail.shipper?.address,
            contact: (detail.shipper as any)?.contact,
            country: extractCountry(detail.shipper?.address),
            address_line_1: detail.shipper?.address_line_1,
            address_line_2: detail.shipper?.address_line_2,
            address_line_3: detail.shipper?.address_line_3,
            city: detail.shipper?.city,
            postcode: detail.shipper?.postcode,
            eori: (detail.shipper as any)?.eori,
          },
          {
            source: `detail_${detailIndex}`,
            type: 'consignee' as const,
            name: detail.consignee?.name,
            address: detail.consignee?.address,
            contact: detail.consignee?.contact,
            country: extractCountry(detail.consignee?.address),
            address_line_1: detail.consignee?.address_line_1,
            address_line_2: detail.consignee?.address_line_2,
            address_line_3: detail.consignee?.address_line_3,
            city: detail.consignee?.city,
            postcode: detail.consignee?.postcode,
            eori: (detail.consignee as any)?.eori,
            vat_no: detail.consignee?.vat_no,
          }
        )
      })

      console.log('[AddressValidationStep] Collected pairs:', allPairs)

      // Deduplicate (by name + country, not by source)
      const pairMap = new Map<string, {
        source: string
        type: 'shipper' | 'consignee'
        name: string
        address?: string | null
        contact?: string | null
        country: string
        address_line_1?: string | null
        address_line_2?: string | null
        address_line_3?: string | null
        city?: string | null
        postcode?: string | null
        eori?: string | null
        vat_no?: string | null
        count: number
      }>()
      allPairs.forEach(({ source, type, name, address, contact, country, address_line_1, address_line_2, address_line_3, city, postcode, eori, vat_no }) => {
        if (!name) return
        const key = getNormalizedKey(name, country)
        const existing = pairMap.get(key)
        if (existing) {
          existing.count++
        } else {
          pairMap.set(key, {
            source,
            type,
            name,
            address,
            contact,
            country: country || '',
            address_line_1,
            address_line_2,
            address_line_3,
            city,
            postcode,
            eori,
            vat_no,
            count: 1,
          })
        }
      })

      console.log('[AddressValidationStep] Unique pairs:', Array.from(pairMap.entries()))

      // Fetch all customers for matching — address fields included so a name match can be
      // cross-checked against the BL-parsed address, not just accepted on name alone (K2).
      // Z88: paged -- an unpaged read is silently capped at 1000 rows, which here would make the
      // matcher miss existing customers and offer to create duplicates.
      const allCustomers = await fetchAllRows<{
        id: string; name: string; country: string | undefined; code: string
        address_line_1: string | null; address_line_2: string | null; address_line_3: string | null
        city: string | null; postcode: string | null
      }>((from, to) =>
        supabase
          .from('customers')
          .select('id, name, country, code, address_line_1, address_line_2, address_line_3, city, postcode')
          .eq('is_active', true)
          .order('name').order('id')
          .range(from, to))

      console.log('[AddressValidationStep] All customers from DB:', allCustomers?.length, allCustomers?.slice(0, 3))
      setAllCustomers((allCustomers || []).map(c => ({ id: c.id, name: c.name, code: c.code })))
      setCustomerAddrById(Object.fromEntries((allCustomers || []).map(c => [
        c.id,
        [c.address_line_1, c.address_line_2, c.address_line_3, c.city, c.postcode, c.country].filter(Boolean).join(', '),
      ])))

      // Address token string for containment comparison — mirrors the CI-verification
      // approach in src/lib/verification/normalize.ts, but here both sides are structured
      // fields (DB row vs BL-parsed pair) rather than one side being free text.
      function addressTokens(a: { address_line_1?: string | null; city?: string | null; postcode?: string | null }): string {
        return [a.address_line_1, a.city, a.postcode].filter(Boolean).join(' ')
      }
      function formatCustomerAddress(c: { address_line_1?: string | null; address_line_2?: string | null; address_line_3?: string | null; city?: string | null; postcode?: string | null; country?: string | null }): string {
        return [c.address_line_1, c.address_line_2, c.address_line_3, c.city, c.postcode, c.country].filter(Boolean).join(', ')
      }
      // If either side has no address data at all, don't block the match on it (same
      // "blank = wildcard" convention already used for country above) — otherwise require
      // the tokens to line up in at least one direction.
      function addressesMatch(c: { address_line_1?: string | null; city?: string | null; postcode?: string | null }, pairAddr: { address_line_1?: string | null; city?: string | null; postcode?: string | null }): boolean {
        const cTokens = addressTokens(c)
        const pTokens = addressTokens(pairAddr)
        if (!cTokens || !pTokens) return true
        return containsAllTokens(cTokens, pTokens) || containsAllTokens(pTokens, cTokens)
      }

      // Match each address
      for (const [, pair] of pairMap) {
        let status: 'new' | 'matched' | 'ambiguous' | 'address_mismatch' = 'new'
        let matched = undefined
        let candidates = undefined

        if (allCustomers) {
          // Strict match: name + country, case-insensitive, trim
          const strictMatches = allCustomers.filter(c =>
            c.name?.toLowerCase().trim() === pair.name.toLowerCase().trim() &&
            countriesMatch(c.country, pair.country)
          )

          console.log(`[AddressValidationStep] Strict match for "${pair.name}" (country: "${pair.country}"):`, strictMatches.length)

          if (strictMatches.length === 1) {
            if (addressesMatch(strictMatches[0], pair)) {
              status = 'matched'
              matched = {
                id: strictMatches[0].id,
                name: strictMatches[0].name,
                code: strictMatches[0].code,
              }
            } else {
              status = 'address_mismatch'
              matched = {
                id: strictMatches[0].id,
                name: strictMatches[0].name,
                code: strictMatches[0].code,
                addressDisplay: formatCustomerAddress(strictMatches[0]),
              }
            }
          } else if (strictMatches.length > 1) {
            status = 'ambiguous'
            candidates = strictMatches.map(c => ({
              id: c.id,
              name: c.name,
              code: c.code,
            }))
          } else {
            // Fuzzy match: remove suffixes
            const fuzzyName = fuzzyMatch(pair.name)
            console.log(`[AddressValidationStep] Fuzzy matching "${pair.name}" -> "${fuzzyName}"`)
            const fuzzyMatches = allCustomers.filter(c => {
              const cFuzzy = fuzzyMatch(c.name)
              // Same wildcard-on-blank + UK-alias treatment as the strict match above (#296) --
              // this branch had been left on a raw strict-equality country check, which blocked
              // otherwise-identical long names whenever country extraction came back blank/differently
              // worded on one side (the exact scenario #296 already fixed for the strict-match path).
              return (
                cFuzzy.toLowerCase().trim() === fuzzyName.toLowerCase().trim() &&
                countriesMatch(c.country, pair.country)
              )
            })

            console.log(`[AddressValidationStep] Fuzzy match for "${fuzzyName}" (country: "${pair.country}"):`, fuzzyMatches.length)

            if (fuzzyMatches.length === 1) {
              if (addressesMatch(fuzzyMatches[0], pair)) {
                status = 'matched'
                matched = {
                  id: fuzzyMatches[0].id,
                  name: fuzzyMatches[0].name,
                  code: fuzzyMatches[0].code,
                }
              } else {
                status = 'address_mismatch'
                matched = {
                  id: fuzzyMatches[0].id,
                  name: fuzzyMatches[0].name,
                  code: fuzzyMatches[0].code,
                  addressDisplay: formatCustomerAddress(fuzzyMatches[0]),
                }
              }
            } else if (fuzzyMatches.length > 1) {
              status = 'ambiguous'
              candidates = fuzzyMatches.map(c => ({
                id: c.id,
                name: c.name,
                code: c.code,
              }))
            }
          }
        }

        // Prevent-duplicate-creation aid: the exact/fuzzy matcher above only catches
        // near-identical names. If it came up empty, take one more pass scoring every
        // active customer by name similarity — surfaced as a "Suggested match" staff can
        // accept with one click instead of the AI flow silently creating a near-duplicate.
        let suggestedMatch: UniqueAddress['suggestedMatch']
        let autoSelectedCustomerId: string | undefined
        if (status === 'new' && allCustomers) {
          let best: { id: string; name: string; code: string; score: number } | null = null
          for (const c of allCustomers) {
            const score = nameSimilarity(pair.name, c.name)
            if (score >= SUGGESTED_MATCH_THRESHOLD && (!best || score > best.score)) {
              best = { id: c.id, name: c.name, code: c.code, score }
            }
          }
          if (best) {
            suggestedMatch = best
            // >=95% similar: apply it automatically instead of making staff click "Use this" --
            // same effect as handleSelectCustomer, just applied up front.
            if (best.score >= AUTO_SELECT_THRESHOLD) {
              status = 'matched'
              autoSelectedCustomerId = best.id
            }
          }
        }

        // A3/Z52: pre-fill the manual mapping panel from whatever structured fields the AI
        // extraction gave us, falling back to a client-side parse of the raw block for
        // anything still blank (mirrors create-autojobs' parseAddress -- see quickParseAddress).
        const parsedGuess = quickParseAddress(pair.address)
        const manualAddress: AddressMappingFields = {
          address_line_1: pair.address_line_1 || parsedGuess.address_line_1,
          address_line_2: pair.address_line_2 || parsedGuess.address_line_2,
          address_line_3: pair.address_line_3 || parsedGuess.address_line_3,
          address_line_4: parsedGuess.address_line_4,
          city: pair.city || parsedGuess.city,
          county: parsedGuess.county,
          postcode: pair.postcode || parsedGuess.postcode,
          country: resolveCountryCode(pair.country) || parsedGuess.country,
        }

        const mapKey = getMapKey(pair.type, pair.name)
        uniqueAddresses.set(mapKey, {
          key: mapKey,
          type: pair.type as 'shipper' | 'consignee',
          name: pair.name,
          address: pair.address,
          contact: pair.contact,
          country: pair.country,
          address_line_1: pair.address_line_1,
          address_line_2: pair.address_line_2,
          address_line_3: pair.address_line_3,
          city: pair.city,
          postcode: pair.postcode,
          eori: pair.eori,
          vat_no: pair.vat_no ? String(pair.vat_no).toUpperCase().replace(/\s+/g, '') : undefined,
          count: pair.count,
          status,
          matchedCustomer: matched,
          candidates,
          suggestedMatch,
          selectedCustomerId: autoSelectedCustomerId,
          manualAddress,
          addressManuallyVerified: false,
        })
      }

      setAddresses(Array.from(uniqueAddresses.values()))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze addresses')
    } finally {
      setLoading(false)
    }
  }

  function handleResolveMismatch(key: string, resolution: 'use_existing' | 'use_bl') {
    setAddresses(prev =>
      prev.map(addr =>
        addr.key === key ? { ...addr, mismatchResolution: resolution } : addr
      )
    )
  }

  function handleEoriChange(key: string, value: string) {
    setAddresses(prev => prev.map(addr => addr.key === key ? { ...addr, eori: value.toUpperCase().replace(/\s+/g, '') } : addr))
  }

  function handleVatChange(key: string, value: string) {
    setAddresses(prev => prev.map(addr => addr.key === key ? { ...addr, vat_no: value.toUpperCase().replace(/\s+/g, '') } : addr))
  }

  function blAddressText(addr: UniqueAddress): string {
    return [addr.address_line_1, addr.address_line_2, addr.address_line_3, addr.city, addr.postcode, addr.country].filter(Boolean).join(', ') || addr.address || ''
  }

  function handleManualAddressChange(key: string, fields: AddressMappingFields) {
    setAddresses(prev =>
      prev.map(addr =>
        addr.key === key ? { ...addr, manualAddress: fields, addressManuallyVerified: true } : addr
      )
    )
  }

  // A3/Z52: this card is about to create/overwrite a customer_addresses row, so it needs the
  // manual mapping panel -- an existing-customer match (however it got resolved) just reuses
  // whatever Operations address that customer already has, no prompt.
  function needsManualAddress(addr: UniqueAddress): boolean {
    if (addr.status === 'new' && !addr.selectedCustomerId) return true
    if (addr.status === 'address_mismatch' && addr.mismatchResolution === 'use_bl') return true
    return false
  }

  function handleSelectCustomer(key: string, customerId: string) {
    setAddresses(prev =>
      prev.map(addr =>
        addr.key === key
          ? { ...addr, selectedCustomerId: customerId, status: 'matched' as const }
          : addr
      )
    )
  }

  function handleCreateAll() {
    setAddresses(prev =>
      prev.map(addr => ({ ...addr, status: 'new' as const, selectedCustomerId: undefined }))
    )
  }

  function handleContinue() {
    // Build addressDecisions array
    const decisions = addresses.map(addr => {
      // A3/Z52: for a card that went through the manual mapping panel, staff's corrected
      // fields are authoritative -- send those instead of the raw AI extraction, and mark
      // the row as reviewed so upsertOperationsAddress won't skip a later refresh of it.
      const manual = needsManualAddress(addr) ? addr.manualAddress : undefined
      return {
        key: addr.key,
        type: addr.type,
        name: addr.name,
        address: addr.address,
        contact: addr.contact,
        country: manual ? manual.country : addr.country,
        address_line_1: manual ? manual.address_line_1 : (addr as any)?.address_line_1,
        address_line_2: manual ? manual.address_line_2 : (addr as any)?.address_line_2,
        address_line_3: manual ? manual.address_line_3 : (addr as any)?.address_line_3,
        address_line_4: manual ? manual.address_line_4 : undefined,
        county: manual ? manual.county : undefined,
        city: manual ? manual.city : (addr as any)?.city,
        postcode: manual ? manual.postcode : (addr as any)?.postcode,
        eori: (addr as any)?.eori,
        vat_no: addr.vat_no || undefined,
        address_manually_verified: !!manual,
        action: addr.status === 'address_mismatch'
          ? (addr.mismatchResolution === 'use_bl' ? 'update_address' : 'use_existing')
          : addr.selectedCustomerId
            ? 'use_existing'
            : addr.status === 'matched'
              ? 'use_existing'
              : 'create_new',
        selectedCustomerId:
          addr.status === 'address_mismatch'
            ? addr.matchedCustomer?.id
            : (addr.selectedCustomerId ||
              (addr.status === 'matched' ? addr.matchedCustomer?.id : undefined)),
      }
    })

    // Build customerMappings for additional roles (notify_party, invoice_to, etc.)
    // For now, these are empty - user can set via workflow UI later
    const customerMappings: CustomerMapping[] = []

    onContinue(decisions, customerMappings)
  }

  const hasAmbiguous = addresses.some(a => a.status === 'ambiguous' && !a.selectedCustomerId)
  const hasUnresolvedMismatch = addresses.some(a => a.status === 'address_mismatch' && !a.mismatchResolution)
  // A3/Z52: block Continue until every new-address card has a Country picked -- mirrors the
  // "Save disabled until Country selected" gate on the panel itself.
  const hasUnverifiedNewAddress = addresses.some(a => needsManualAddress(a) && !a.manualAddress?.country)
  // customers.vat_no is varchar(12) with a CDS CHECK: a longer value would fail at save with a database error (and must never be cut silently)
  const hasBadVat = addresses.some(a => (a.vat_no ?? '').length > VAT_MAX)

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Step 5: Address Validation</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Verify and match shipper/consignee addresses with existing customers
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-4 rounded-lg text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin">⏳</div>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Analyzing addresses...</p>
        </div>
      ) : addresses.length === 0 ? (
        <div className="text-center py-8 text-gray-600 dark:text-gray-400">
          No addresses to validate
        </div>
      ) : (
        <>
          <div className="flex gap-2 mb-4">
            <button
              onClick={handleCreateAll}
              className="px-3 py-2 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-lg"
            >
              Create All New
            </button>
            <button
              onClick={() => setAddresses(prev => prev.filter(a => a.status !== 'ambiguous'))}
              className="px-3 py-2 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg"
            >
              Skip Ambiguous
            </button>
          </div>

          <div className="space-y-3">
            {addresses.map(addr => (
              <div
                key={addr.key}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">
                        {addr.status === 'new'
                          ? '🆕'
                          : addr.status === 'matched'
                            ? '✅'
                            : '⚠️'}
                      </span>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                          <span>{addr.type === 'shipper' ? '📦' : '🏢'} {addr.name}</span>
                          {/* Same arrow as the job page. A company that will only be created on confirm has no page yet: shown disabled, with the reason. */}
                          <CompanyLink
                            customerId={addr.selectedCustomerId || (addr.status === 'matched' || addr.status === 'address_mismatch' ? addr.matchedCustomer?.id : undefined)}
                            disabledTitle={addr.status === 'ambiguous' ? 'Choose a customer first' : 'This company is created when you confirm; open it afterwards'}
                          />
                        </p>
                        {addr.country && (
                          <p className="text-xs text-gray-600 dark:text-gray-400">{addr.country}</p>
                        )}
                        {addr.count > 1 && (
                          <p className="text-xs text-blue-600 dark:text-blue-400">
                            Shared across {addr.count}{' '}
                            {addr.type === 'shipper' ? 'HBLs' : 'HBLs'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex-1">
                    {addr.status === 'matched' && addr.matchedCustomer && !addr.selectedCustomerId && (
                      <div className="text-sm bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 p-2 rounded">
                        Matched: {addr.matchedCustomer.name}
                      </div>
                    )}
                    {addr.status === 'ambiguous' && (
                      <div className="space-y-2">
                        <p className="text-xs text-gray-600 dark:text-gray-400 font-semibold">
                          Select customer:
                        </p>
                        <select
                          value={addr.selectedCustomerId || ''}
                          onChange={e => handleSelectCustomer(addr.key, e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="">-- Choose or Create --</option>
                          {addr.candidates?.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.name} ({c.code}){customerAddrById[c.id] ? ` — ${customerAddrById[c.id]}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {addr.status === 'address_mismatch' && addr.matchedCustomer && (
                      <div className="space-y-2 text-sm">
                        <p className="text-xs text-amber-700 dark:text-amber-400 font-semibold">
                          Name matches "{addr.matchedCustomer.name}" but the address on file differs from the BL — pick one:
                        </p>
                        <button
                          onClick={() => handleResolveMismatch(addr.key, 'use_existing')}
                          className={`w-full text-left px-3 py-2 border rounded-lg hover:bg-white dark:hover:bg-gray-700 ${
                            addr.mismatchResolution === 'use_existing'
                              ? 'border-blue-500 ring-1 ring-blue-500'
                              : 'border-gray-300 dark:border-gray-600'
                          }`}
                        >
                          <span className="font-medium">Use existing address book entry:</span>{' '}
                          {addr.matchedCustomer.addressDisplay || '(no address on file)'}
                        </button>
                        <button
                          onClick={() => handleResolveMismatch(addr.key, 'use_bl')}
                          className={`w-full text-left px-3 py-2 border rounded-lg hover:bg-white dark:hover:bg-gray-700 ${
                            addr.mismatchResolution === 'use_bl'
                              ? 'border-blue-500 ring-1 ring-blue-500'
                              : 'border-gray-300 dark:border-gray-600'
                          }`}
                        >
                          <span className="font-medium">Use address from BL/HBL:</span>{' '}
                          {/* Z48: structured fields are empty for every AI/PDF-parsed BL --
                              fall back to the raw parsed block so staff aren't shown just
                              the country alone before confirming this overwrite. */}
                          {[addr.address_line_1, addr.address_line_2, addr.address_line_3, addr.city, addr.postcode, addr.country].filter(Boolean).join(', ') || addr.address || '(no address parsed)'}
                        </button>
                      </div>
                    )}
                    {addr.status === 'new' && !addr.selectedCustomerId && (
                      <div className="space-y-2">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Will create new customer</div>
                        {addr.suggestedMatch && (
                          <div className="text-sm bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 p-2 rounded flex items-center justify-between gap-2">
                            <span>
                              Suggested match: <strong>{addr.suggestedMatch.name}</strong> ({addr.suggestedMatch.code}) — {Math.round(addr.suggestedMatch.score * 100)}% similar
                            </span>
                            <button
                              onClick={() => handleSelectCustomer(addr.key, addr.suggestedMatch!.id)}
                              className="shrink-0 px-2 py-1 text-xs bg-amber-600 hover:bg-amber-700 text-white rounded"
                            >
                              Use this
                            </button>
                          </div>
                        )}
                        {addr.suggestedMatch && (
                          <AddressCompare
                            left={blAddressText(addr)}
                            right={customerAddrById[addr.suggestedMatch.id] || ''}
                            rightLabel={`${addr.suggestedMatch.name} (${addr.suggestedMatch.code})`}
                          />
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <label className="block">
                            <span className="text-xs text-gray-600 dark:text-gray-400">EORI (new company)</span>
                            <input
                              type="text"
                              value={addr.eori || ''}
                              onChange={e => handleEoriChange(addr.key, e.target.value)}
                              placeholder="e.g. GB123456789000"
                              className="mt-1 w-full px-2 py-1 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs text-gray-600 dark:text-gray-400">VAT No (new company)</span>
                            <input
                              type="text"
                              value={addr.vat_no || ''}
                              onChange={e => handleVatChange(addr.key, e.target.value)}
                              placeholder="e.g. 123456789"
                              aria-invalid={(addr.vat_no ?? '').length > VAT_MAX}
                              className={`mt-1 w-full px-2 py-1 text-sm font-mono border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                                (addr.vat_no ?? '').length > VAT_MAX ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                              }`}
                            />
                            {(addr.vat_no ?? '').length > VAT_MAX && (
                              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                A VAT number is at most {VAT_MAX} characters (the customs limit). Shorten it to continue.
                              </p>
                            )}
                          </label>
                        </div>
                        {!addr.eori && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            No EORI found in the document. If we clear this shipment, add it now; otherwise leave it blank (it can be added later on the company page). This does not stop you continuing.
                          </p>
                        )}
                      </div>
                    )}
                    {addr.selectedCustomerId && addr.status !== 'ambiguous' && (
                      <div className="text-sm bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 p-2 rounded">
                        Using: {allCustomers.find(c => c.id === addr.selectedCustomerId)?.name || addr.selectedCustomerId}
                        <div className="mt-2">
                          <AddressCompare left={blAddressText(addr)} right={customerAddrById[addr.selectedCustomerId] || ''} />
                        </div>
                      </div>
                    )}

                    {needsManualAddress(addr) && addr.manualAddress && (
                      <AddressMappingPanel
                        rawText={addr.address}
                        fields={addr.manualAddress}
                        onChange={f => handleManualAddressChange(addr.key, f)}
                      />
                    )}

                    {/* Y1: lets staff redirect ANY address to an existing customer, not just
                        the pre-filtered candidates offered for 'ambiguous' above -- this is
                        the actual duplicate-creation guard for the common 'new' case. */}
                    <div className="mt-2">
                      {replacingKey === addr.key ? (
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <SearchableSelect
                              options={allCustomers.map(c => ({ id: c.id, label: `${c.name} (${c.code})` }))}
                              value={addr.selectedCustomerId || ''}
                              onChange={id => { handleSelectCustomer(addr.key, id); setReplacingKey(null) }}
                              placeholder="Search companies..."
                            />
                          </div>
                          <button
                            onClick={() => setReplacingKey(null)}
                            className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setReplacingKey(addr.key)}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          🔁 Replace from address book
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={onBack}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Back
            </button>
            <button
              onClick={handleContinue}
              disabled={hasAmbiguous || hasUnresolvedMismatch || hasUnverifiedNewAddress || hasBadVat}
              className={`flex-1 px-4 py-2 rounded-lg font-medium text-white transition ${
                hasAmbiguous || hasUnresolvedMismatch || hasUnverifiedNewAddress
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              Continue to Job Creation
            </button>
          </div>
        </>
      )}
    </div>
  )
}
